import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from "../contexts/AuthContext";
import TopNav from '../components/TopNav';
import {
  ShieldIcon,
  KeyIcon,
  GlobeIcon,
  ArrowRightIcon,
  UserIcon,
  CheckIcon
} from '../components/Icons';

export default function Home() {
  const { isAuthenticated, user, setSession } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (event) => {
      const allowedTypes = ['OAUTH_AUTH_SUCCESS', 'LOGIN_SUCCESS', 'ONE_LOGIN_AUTH_SUCCESS', 'login_success'];
      const data = event?.data || {};

      if (!allowedTypes.includes(data?.type)) {
        return;
      }

      const payload = data.payload || data || {};
      const token = payload.accessToken || payload.token || payload.access_token || payload.authorizationCode || null;
      const refreshToken = payload.refreshToken || payload.refresh_token || null;
      const userObj = payload.user || payload.userData || payload.user_info || null;

      if (!token) {
        console.warn('Received auth message but no token found; ignoring');
        return;
      }

      localStorage.setItem('access_token', token);
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      if (userObj) {
        localStorage.setItem('user', JSON.stringify(userObj));
      }

      if (setSession) {
        setSession(token, refreshToken, userObj);
      }

      navigate('/dashboard');
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [setSession, navigate]);

  const openAuthPopup = () => {
    window.open('/auth/popup', 'AuthPopup', 'width=500,height=650');
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 flex flex-col">
      <TopNav />

      {/* Hero Section Container matching Dashboard max-w-7xl and padding */}
      <main className="flex-1 max-w-7xl mx-auto px-6 py-12 w-full space-y-16">
        <section className="text-center max-w-3xl mx-auto py-8">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 text-xs font-semibold mb-6 shadow-sm">
            <ShieldIcon className="w-4 h-4" />
            <span>Next-Gen Enterprise Single Sign-On</span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-6 text-slate-900 dark:text-white leading-tight">
            One-Login — Identity & Access, simplified
          </h1>
          
          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
            Centralize identity, single sign-on, and session security across all your applications.
            Pure JWT architecture with multi-account support and OTP verification.
          </p>

          {!isAuthenticated ? (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={openAuthPopup}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-indigo-600 text-white font-semibold px-6 py-3 rounded-xl shadow-lg shadow-indigo-600/25 hover:bg-indigo-700 transition-all text-sm"
              >
                <span>Get Started</span>
                <ArrowRightIcon className="w-4 h-4" />
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('features');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all text-sm"
              >
                Learn More
              </button>
            </div>
          ) : (
            <div className="max-w-xl mx-auto bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800/60 rounded-2xl p-6 shadow-xl text-left flex items-start gap-4">
              <div className="p-3 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-xl">
                <UserIcon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 dark:text-white text-base">
                  Signed in as {user?.first_name ? `${user.first_name} ${user.last_name || ''}` : user?.username}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Access active sessions, manage multi-accounts, and view profile settings.
                </p>
                <div className="mt-4">
                  <Link
                    to="/u/0/dashboard/overview"
                    className="inline-flex items-center gap-2 bg-indigo-600 text-white font-semibold text-xs px-4 py-2 rounded-xl shadow-md shadow-indigo-600/20 hover:bg-indigo-700 transition-colors"
                  >
                    <span>Go to Dashboard</span>
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Feature Cards */}
        <section id="features" className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
          <div className="p-6 bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mb-4">
              <GlobeIcon className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-slate-900 dark:text-white text-base mb-2">Identity Management</h3>
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              Manage users, verified emails, phone numbers, and social identities from a unified control plane.
            </p>
          </div>

          <div className="p-6 bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-4">
              <ShieldIcon className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-slate-900 dark:text-white text-base mb-2">Session & Security</h3>
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              Pure JWT architecture without cookies. Instant account switching, session revocation, and multi-tenant security.
            </p>
          </div>

          <div className="p-6 bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center mb-4">
              <KeyIcon className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-slate-900 dark:text-white text-base mb-2">Single Sign-On</h3>
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              Enable seamless SSO for internal and external enterprise applications with Google OAuth and JWT tokens.
            </p>
          </div>
        </section>
      </main>

      {/* Footer matching Dashboard layout footer */}
      <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 p-8 mt-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-4">Enterprise Clients</h3>
            <ul className="space-y-2">
              {['Hause Pro', 'Property Pro', 'Enterprise Solutions Inc', 'Digital Gateway Corp'].map((client) => (
                <li key={client} className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-2">
                  <CheckIcon className="w-3.5 h-3.5 text-emerald-500" />
                  <span>{client}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-4">Product</h3>
            <ul className="space-y-2">
              <li className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600 cursor-pointer">Features</li>
              <li>
                <Link to="/about" className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600">About Us</Link>
              </li>
              <li className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600 cursor-pointer">Security</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-4">Support</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/help" className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600">Help Center</Link>
              </li>
              <li className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600 cursor-pointer">Documentation</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-4">Legal</h3>
            <ul className="space-y-2">
              <li className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600 cursor-pointer">Privacy Policy</li>
              <li>
                <Link to="/contact" className="text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600">Support Contact</Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 text-center text-xs text-slate-500">
          <p>&copy; 2026 OneLogin SSO Platform. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
